import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { libraryApi } from '../../lib/api/libraryApi';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';

const AIInterviewViewModal = ({ open, interviewId, onClose, orgId = null }) => {
  useBodyScrollLock(open);
  const [expandedIntro, setExpandedIntro] = useState(true);
  const [expandedOutro, setExpandedOutro] = useState(true);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);

  useEffect(() => {
    if (open) {
      setExpandedIntro(true);
      setExpandedOutro(true);
      setExpandedQuestionIndex(null);
    }
  }, [open, interviewId]);

  const { data, isLoading } = useQuery({
    queryKey: ['library-ai-interview', orgId, interviewId],
    queryFn: () => libraryApi.getAIInterviewById(interviewId, { orgId }),
    enabled: open && Boolean(interviewId),
    staleTime: 2 * 60 * 1000,
  });

  if (!open || !interviewId) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
      <div className="fixed inset-0" onClick={onClose} aria-hidden />
      <div
        className="relative flex flex-col w-full max-w-4xl h-[90vh] max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ minHeight: '580px' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          ✕
        </button>
        <div className="flex flex-1 min-h-0">
          <div className="w-56 bg-gray-50 border-r border-gray-200 flex flex-col">
            <div className="px-4 py-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">View Interview</h3>
              <p className="mt-1 text-xs text-gray-500">Read-only interview flow</p>
            </div>
            <div className="px-3 py-3">
              <div className="w-full rounded-md px-3 py-2 bg-[#007BFF]/10 text-[#007BFF] text-sm font-medium">
                View
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white overflow-hidden">
            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white">
              <h2 className="text-lg font-semibold text-gray-900 truncate pr-12">
                {isLoading ? 'Loading…' : data?.title || 'AI Interview'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {isLoading
                  ? 'Fetching interview details…'
                  : `Questions: ${data?.advancedType === 'rubric-based' && Array.isArray(data?.rubrics) ? data.rubrics.reduce((sum, m) => sum + Math.min(m.questionsPerInterview || 1, Array.isArray(m.questionBank) ? m.questionBank.length : 0), 0) : (data?.questions?.length ?? 0)} • Duration: ${data?.estimatedTime ?? data?.duration ?? 0} min`}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-24">
                  <div className="h-10 w-10 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-3 pb-4">
                  <IntroOrOutroCard
                    title="Introduction Message"
                    icon={<Sparkles className="w-5 h-5 text-[#007BFF]" />}
                    content={data?.aiIntro || 'No introduction message saved.'}
                    expanded={expandedIntro}
                    onToggle={() => setExpandedIntro((prev) => !prev)}
                  />

                  {data?.advancedType === 'rubric-based' && Array.isArray(data?.rubrics) && data.rubrics.length > 0 ? (
                    <div className="space-y-4">
                      {data.rubrics.map((module, mIdx) => (
                        <div key={module._id || mIdx} className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-800">{module.name || 'Unnamed Module'}</h4>
                              <p className="text-xs text-gray-500 mt-0.5">{module.questionBank?.length || 0} questions in bank</p>
                            </div>
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                              Pick {module.questionsPerInterview}
                            </span>
                          </div>
                          <div className="p-4 space-y-3">
                            {module.questionBank?.map((q, qIdx) => (
                              <div key={q._id || qIdx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex gap-3">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                                  {qIdx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800">{q.prompt || q.question}</p>
                                  <div className="mt-1.5 flex gap-2 flex-wrap">
                                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                                      {q.type === 'yes_no' ? 'Yes/No' : 'Short answer'}
                                    </span>
                                    {q.critical && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded border border-red-200">Critical</span>}
                                    {q.mandatory && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200">Mandatory</span>}
                                  </div>
                                  {q.expectedAnswer && (
                                    <p className="mt-2 text-xs text-gray-600 italic">
                                      <span className="font-semibold not-italic">Expected: </span>
                                      {q.expectedAnswer}
                                    </p>
                                  )}
                                  {Array.isArray(q.expectedPoints) && q.expectedPoints.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Scoring Rubric Points:</p>
                                      {q.expectedPoints.map((point, pIdx) => (
                                        <div key={pIdx} className="flex gap-2 text-xs text-gray-700">
                                          <span className="text-indigo-500 font-bold">•</span>
                                          <span>{typeof point === 'string' ? point : point?.text || ''}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    (data?.questions || []).map((q, i) => (
                      <QuestionCard
                        key={`${q.prompt}-${i}`}
                        question={q}
                        index={i}
                        expanded={expandedQuestionIndex === i}
                        onToggle={() =>
                          setExpandedQuestionIndex((prev) => (prev === i ? null : i))
                        }
                      />
                    ))
                  )}

                  <IntroOrOutroCard
                    title="Closing Message"
                    icon={<CheckIcon className="w-5 h-5 text-[#007BFF]" />}
                    content={data?.aiOutro || 'No closing message saved.'}
                    expanded={expandedOutro}
                    onToggle={() => setExpandedOutro((prev) => !prev)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const IntroOrOutroCard = ({ title, icon, content, expanded, onToggle }) => (
  <div className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between p-4">
      <button type="button" onClick={onToggle} className="flex items-center gap-3 text-left min-w-0">
        {icon}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500 line-clamp-1">{content}</p>
        </div>
      </button>
      <button
        type="button"
        onClick={onToggle}
        className="p-1 text-gray-400 hover:text-gray-600 rounded"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse section' : 'Expand section'}
      >
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </div>
    <div className={clsx('overflow-hidden transition-[max-height] duration-200 ease-out', expanded ? 'max-h-[240px]' : 'max-h-0')}>
      <div className="p-4 border-t border-gray-100">
        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-700 whitespace-pre-line">
          {content}
        </div>
      </div>
    </div>
  </div>
);

const sectionColor = (section) => {
  if (section === 'eligibility') return 'bg-yellow-100 text-yellow-700';
  if (section === 'behavioral') return 'bg-green-100 text-green-700';
  if (section === 'cultural') return 'bg-purple-100 text-purple-700';
  return 'bg-blue-100 text-blue-700';
};

const QuestionCard = ({ question, index, expanded, onToggle }) => (
  <div className="p-4 rounded-lg border border-gray-200 bg-white">
    <div className="flex items-start justify-between gap-3">
      <button type="button" onClick={onToggle} className="flex-1 text-left">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold bg-[#007BFF] text-white flex-shrink-0">
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm break-words">{question.prompt}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className={clsx(
                  'text-xs px-2 py-0.5 rounded-full font-medium',
                  sectionColor(question.section)
                )}
              >
                {question.section === 'eligibility'
                  ? 'Eligibility'
                  : question.section === 'behavioral'
                    ? 'Behavioral'
                    : question.section === 'cultural'
                      ? 'Cultural'
                      : 'Technical'}
              </span>
              {question.type === 'yes_no' && question.critical && (
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 border border-red-200">
                  Critical
                </span>
              )}
              <span className="text-xs text-gray-500">
                {question.type === 'yes_no' ? 'Yes/No' : 'Detailed'}
              </span>
            </div>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={onToggle}
        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse question' : 'Expand question'}
      >
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </div>

    <div className={clsx('overflow-hidden transition-[max-height] duration-200 ease-out', expanded ? 'max-h-[600px]' : 'max-h-0')}>
      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-700">
          {question.prompt}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Type:</span>
          <span className="text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
            {question.type === 'yes_no' ? 'Yes/No' : 'Detailed Answer'}
          </span>
        </div>

        {question.type === 'yes_no' && question.expectedAnswer && (
          <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100">
            <label className="block text-xs font-medium text-indigo-700 mb-2">Expected Answer</label>
            <p className="text-sm font-medium text-gray-700">{question.expectedAnswer}</p>
          </div>
        )}

        {question.type !== 'yes_no' && (
          <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100 space-y-2">
            <label className="block text-xs font-medium text-indigo-700">Expected Answer Points</label>
            {Array.isArray(question.expectedPoints) && question.expectedPoints.length > 0 ? (
              <div className="space-y-2">
                {question.expectedPoints.map((point, idx) => (
                  <div key={`${point}-${idx}`} className="flex items-start gap-2">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
                      {idx + 1}
                    </span>
                    <p className="text-sm text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-600">No expected points added.</p>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default AIInterviewViewModal;
